import type { MutationKey, QueryKey, UseQueryOptions, UseQueryResult, UseSuspenseQueryOptions, UseSuspenseQueryResult } from '@tanstack/react-query';
import { useMutationState, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { POLLING_DEFAULTS, resolvePollingInterval, type PollingEnvironmentOverrides, type PollingQueryStatus } from '@/lib/query/polling';

/**
 * 直近ミューテーションの間だけ短い間隔で再フェッチするブースト設定。
 *
 * 「自分の操作直後はすぐ反映 / 閲覧時は控えめ」というハイブリッド戦略のための仕組み。
 * 内部では `useMutationState` で `mutationKey` プレフィックス一致のミューテーションを
 * subscribe し、`submittedAt` が `windowMs` 以内ならブースト間隔を返す。
 *
 * - `mutationKey`: ブーストのトリガー。対応する `useMutation({ mutationKey })` 側にも
 *   同じキー（または同プレフィックス）を付与する必要がある。
 * - `intervalMs`: ブースト中の短い間隔（ms）。
 * - `windowMs`: 直近ミューテーションの開始時刻（`submittedAt`）からブーストを継続する時間（ms）。
 * - `shouldBoost`: 直近ミューテーションが無くても、現在のキャッシュデータから
 *   ブースト要否を判定するコールバック。例えば「Package を開始した直後は Step が
 *   `InProgress` だが Tasks がまだ `NotStarted`」のような、サーバ側の遷移途中状態を
 *   フロントから検知して短い間隔で取りに行きたいケース。`refetchInterval` のたびに
 *   評価されるので、状態が落ち着いたら自然にベース間隔へ戻る。
 */
export type PollingBoostOptions<TData = unknown> = {
  intervalMs: number;
  windowMs: number;
  mutationKey: MutationKey;
  shouldBoost?: (data: TData | undefined) => boolean;
};

/** `refetchInterval` コールバックから参照する query スライス（TanStack の Query 型は select 後の TData とずれるため） */
export type PollingQueryStateSlice<TData> = {
  state: {
    data: TData | undefined;
    status: PollingQueryStatus;
  };
};

/**
 * ポーリング設定。`TData` は `useQuery` の `select` 後データ（未指定時は `queryFn` 戻り値）に一致する。
 *
 * `usePollingQuery` 側では `polling: PollingQueryOptions<NoInfer<TData>>` とし、
 * `queryFn` / `select` から `TData` を推論させる（`shouldPoll` からは推論しない）。
 */
export type PollingQueryOptions<TData = unknown> = {
  /**
   * 未指定時は {@link POLLING_DEFAULTS.intervalMs}
   */
  intervalMs?: number;
  /**
   * true のときだけ `refetchInterval` を有効にする（ドメインの終了条件など）
   *
   * 第1引数は `select` 後の `TData`（`usePollingQuery` が `refetchInterval` 内で {@link resolvePollingQueryData} を適用してから渡す）。
   * `select` 未指定時は `TQueryFnData` と同形。
   *
   * 推論が弱いときは `usePollingQuery<TQueryFnData, TError, TData>({ ... })` で明示する。
   */
  shouldPoll: (data: TData | undefined, query: PollingQueryStateSlice<TData>) => boolean;
  environmentOverrides?: PollingEnvironmentOverrides;
  /**
   * 直近ミューテーション後の一定時間だけ短い間隔で再フェッチするハイブリッド設定。
   * 自分の操作の結果をすぐ反映したいが、閲覧中は負荷を抑えたいケースで使う。
   *
   * `shouldBoost` を渡すと、ミューテーションが無くても「キャッシュ上のデータが特定の
   * 遷移途中状態か」でブーストできる。
   */
  boost?: PollingBoostOptions<TData>;
};
type StripPollingKey = 'refetchInterval';

/**
 * `boost` 未指定時に `useMutationState` へ `filters: undefined` を渡すと、
 * キャッシュ上の**すべての**ミューテーションを購読してしまい、
 * 無関係な mutation でもこのフック配下のコンポーネントが再レンダーされる恐れがある。
 * 絶対に本番の `useMutation` と衝突しない、マッチ件数 0 になる専用キーで抑止する。
 */
const POLLING_BOOST_MUTATION_KEY_INACTIVE: MutationKey = ['@dresscode-frontend/polling', 'boost', 'inactive'];

/**
 * `boost.mutationKey` にプレフィックス一致するミューテーションのうち、
 * 最新の `submittedAt` を返す。該当なし or `boost` 未指定時は `null`。
 *
 * `mutationKey` のみ参照するので `TData` には依存しないが、不変ジェネリック型
 * パラメータで呼び出し側の `PollingBoostOptions<TData>` をそのまま受けるため、
 * ここでもジェネリックを維持する。
 */
function useLastBoostMutationAt<TData>(boost: PollingBoostOptions<TData> | undefined): number | null {
  const submittedAtList = useMutationState({
    filters: {
      mutationKey: boost ? boost.mutationKey : POLLING_BOOST_MUTATION_KEY_INACTIVE
    },
    select: m => m.state.submittedAt
  });
  if (!boost || submittedAtList.length === 0) return null;
  return Math.max(...submittedAtList);
}

/**
 * ベース間隔 / ブースト設定 / 直近ミューテーション時刻 / 現在のキャッシュデータから
 * 実効 interval を決定する。
 *
 * - 直近ミューテーションが `windowMs` 以内に存在すればブースト
 * - もしくは `shouldBoost(data)` が真（サーバ側遷移途中などの状態検知）ならブースト
 *
 * `Date.now()` を `refetchInterval` コールバック内で評価することで、ブースト窓の経過は
 * 次のティック発火時に自動的にベース間隔へ戻る（最大で1回分の overshoot あり）。
 * `shouldBoost` も同様に毎回再評価され、状態が変わればベース間隔へ戻る。
 */
function getEffectivePollingIntervalMs<TData>(baseMs: number, boost: PollingBoostOptions<TData> | undefined, lastMutationAt: number | null, data: TData | undefined): number {
  if (!boost) return baseMs;
  const isWithinMutationWindow = lastMutationAt !== null && Date.now() - lastMutationAt < boost.windowMs;
  const isStateBoosted = boost.shouldBoost?.(data) ?? false;
  if (isWithinMutationWindow || isStateBoosted) {
    return boost.intervalMs;
  }
  return baseMs;
}

/**
 * TanStack Query の `refetchInterval` が受け取る `query.state.data` は `select` 適用前の
 * キャッシュ（`TQueryFnData`）。`shouldPoll` / `shouldBoost` 向けに `select` をここで適用する。
 *
 * `refetchInterval` 評価のたびに `select` を再実行する（TanStack observer 用メモ化とは別経路）。
 * 重い変換や副作用は `queryFn` / selector 層に置き、ここでは軽量な unwrap に留めること。
 *
 * `select` が throw した場合は `undefined` を返しポーリングを止める（UI 描画経路の `select` throw とは別）。
 */
export function resolvePollingQueryData<TQueryFnData, TData>(rawData: TQueryFnData | undefined, select: ((data: TQueryFnData) => TData) | undefined): TData | undefined {
  if (rawData === undefined) {
    return undefined;
  }
  if (select) {
    try {
      return select(rawData);
    } catch {
      return undefined;
    }
  }
  return rawData as TData;
}

/**
 * `usePollingQuery` / `usePollingSuspenseQuery` の `refetchInterval` コールバック本体。
 * 第2引数 `query.state.data` は **`resolvePollingQueryData` 適用後の `TData`** を渡すこと。
 */
export function evaluatePollingRefetchInterval<TData>(polling: PollingQueryOptions<TData>, query: PollingQueryStateSlice<TData>, lastMutationAt: number | null): number | false {
  if (!polling.shouldPoll(query.state.data, query)) {
    return false;
  }
  const baseMs = polling.intervalMs ?? POLLING_DEFAULTS.intervalMs;
  const effectiveMs = getEffectivePollingIntervalMs(baseMs, polling.boost, lastMutationAt, query.state.data);
  return resolvePollingInterval(effectiveMs, query, polling.environmentOverrides);
}

/**
 * `useQuery` に定点監視のデフォルト（可視性・オフライン・エラー時停止）と、
 * 任意で「直近ミューテーション後だけ短い間隔にする」ブースト機能を載せる。
 *
 * `polling.shouldPoll` の第1引数は `select` 適用後の `TData`（フック内で {@link resolvePollingQueryData} を通す）。
 */
export function usePollingQuery<TQueryFnData, TError = Error, TData = TQueryFnData, TQueryKey extends QueryKey = QueryKey>(options: Omit<UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>, StripPollingKey> & {
  polling: PollingQueryOptions<NoInfer<TData>>;
}): UseQueryResult<TData, TError> {
  const {
    polling,
    ...queryOptions
  } = options;
  const lastMutationAt = useLastBoostMutationAt(polling.boost);
  return useQuery({
    ...queryOptions,
    refetchInterval: query => evaluatePollingRefetchInterval(polling, {
      state: {
        data: resolvePollingQueryData(query.state.data, queryOptions.select),
        status: query.state.status
      }
    }, lastMutationAt)
  });
}

/**
 * `useSuspenseQuery` 版。`usePollingQuery` と同じブースト機構を持つ。
 *
 * @see {@link usePollingQuery} のジェネリクスと `shouldPoll`（`TData`）について
 */
export function usePollingSuspenseQuery<TQueryFnData, TError = Error, TData = TQueryFnData, TQueryKey extends QueryKey = QueryKey>(options: Omit<UseSuspenseQueryOptions<TQueryFnData, TError, TData, TQueryKey>, StripPollingKey> & {
  polling: PollingQueryOptions<NoInfer<TData>>;
}): UseSuspenseQueryResult<TData, TError> {
  const {
    polling,
    ...queryOptions
  } = options;
  const lastMutationAt = useLastBoostMutationAt(polling.boost);
  return useSuspenseQuery({
    ...queryOptions,
    refetchInterval: query => evaluatePollingRefetchInterval(polling, {
      state: {
        data: resolvePollingQueryData(query.state.data, queryOptions.select),
        status: query.state.status
      }
    }, lastMutationAt)
  });
}